import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import uniqBy from "lodash/uniqBy";
import moment from "moment";

import JoinCampaignBulkSelectionFormBase from "./JoinCampaignBulkSelectionForm";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import {
  displayCreativeNotApplied,
  displayDateNotInRange,
  displaySelectLocation,
} from "./JoinCampaignBulkSelectionMsg";
import { getCreativePackageLabel } from "../../../helpers/creativeLabel";
import {
  getPackageChannelOptions,
  isParticipantChannelSelectionEnabled,
} from "@/features/campaigns/shared/helpers/channelSelectionHelper";

type LocationId = string | number;

type BulkCreative = {
  creative_id: LocationId;
  [key: string]: unknown;
};

type BulkLocation = {
  id: LocationId;
  location_name?: string;
  creatives: BulkCreative[];
  subscription?: {
    subscription_start_date?: unknown;
    subscription_end_date?: unknown;
  };
  location_fees_covered?: boolean;
  budget?: {
    brand_match_preset_amount?: string | number;
  };
  [key: string]: unknown;
};

type BulkFormData = {
  useBudgetRecommendation?: boolean;
  creative?: LocationId[];
  locationBudget?: string | number;
  channels?: LocationId[];
  startDate?: any;
  endDate?: any;
};

type JoinCampaignBulkSelectionProps = {
  onBulkBudgetRecommendationRequested?: (
    _locations: Array<{
      loc: BulkLocation;
      locFormData: Record<string, any>;
    }>
  ) => unknown;
  isBudgetRecommendationEnabled?: boolean;
  values: Record<LocationId, Record<string, any>>;
  setFieldValue: (_field: LocationId, _value: Record<string, any>) => void;
  checkedLocations: LocationId[];
  setCheckedLocations: (_locations: LocationId[]) => void;
  subscribedLocations: BulkLocation[];
  setLocationExpanded: (_locations: LocationId[]) => void;
  setShowCheckbox: (_show: boolean) => void;
  campaign?: Record<string, any>;
};

const JoinCampaignBulkSelectionForm =
  JoinCampaignBulkSelectionFormBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const fireSweetAlert = RpSweetAlert.fire as unknown as (
  _options: Record<string, unknown>
) => Promise<unknown>;

function JoinCampaignBulkSelection({
  values,
  setFieldValue,
  checkedLocations,
  setCheckedLocations,
  subscribedLocations,
  setLocationExpanded,
  setShowCheckbox,
  campaign,
  onBulkBudgetRecommendationRequested,
  isBudgetRecommendationEnabled = false,
}: JoinCampaignBulkSelectionProps) {
  const [expanded, setExpanded] = useState<string | boolean>(false);

  const hideField = useMemo(() => {
    return joinCampaignHelper.hideField(campaign);
  }, [campaign]);

  const handleOnExpand =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      if (isExpanded) setLocationExpanded([]);
      setShowCheckbox(isExpanded);
    };

  const shouldExpandIt = (panel: string) => expanded === panel;

  const getCreatives = useMemo(() => {
    return subscribedLocations?.reduce((acc, l) => {
      const creative = l.creatives.map((c) => ({
        label: getCreativePackageLabel(c),
        value: c.creative_id,
      }));
      return uniqBy(acc?.concat(creative), "value");
    }, []);
  }, [subscribedLocations]);

  const channelSelectionEnabled =
    isParticipantChannelSelectionEnabled(campaign);

  const channelOptions = useMemo(() => {
    return getPackageChannelOptions(
      subscribedLocations?.flatMap((location) => location.creatives || [])
    );
  }, [subscribedLocations]);
  const creativePackages = useMemo(() => {
    return subscribedLocations?.flatMap((location) => location.creatives || []);
  }, [subscribedLocations]);

  const handleBulkSubmit = (formData: BulkFormData) => {
    const {
      creative = [],
      locationBudget,
      channels = [],
      useBudgetRecommendation,
    } = formData;

    if (checkedLocations.length === 0) {
      displaySelectLocation();
      return false; // Return false to prevent form reset
    }

    let isMissedCreatives = false;
    const missedCreativeDetails = [];
    const missedDateDetails = [];
    const appliedRecommendationLocations = [];

    checkedLocations.forEach((location) => {
      const locDetail = subscribedLocations.find(
        (l) => String(l.id) === String(location)
      );

      const availableCreatives = creative.filter((c) => {
        if (locDetail.creatives.find((lc) => lc.creative_id === c)) {
          return true;
        } else {
          missedCreativeDetails.push({
            location_name: locDetail.location_name,
            creative_name: getCreatives?.find((cr) => cr.value === c)?.label,
          });
          return false;
        }
      });

      if (availableCreatives?.length !== creative?.length) {
        isMissedCreatives = true;
      }

      if (formData.startDate) {
        // check if the start date is in the subscription range
        const {
          subscription: { subscription_start_date, subscription_end_date },
        } = locDetail;

        const { minStartDate, maxStartDate } =
          joinCampaignHelper.getCampaignMinMaxStartDate(locDetail, campaign);

        const { minEndDate, maxEndDate } =
          joinCampaignHelper.getCampaignMinMaxEndDate(
            formData.startDate,
            locDetail,
            campaign
          );

        // Check if selected dates are within the subscription date range
        const startDateMoment = moment(formData.startDate);
        const endDateMoment = formData.endDate
          ? moment(formData.endDate)
          : null;
        const subscriptionStartMoment = moment(subscription_start_date);
        const subscriptionEndMoment = moment(subscription_end_date);

        // Check if start date is within subscription range
        const isStartDateInSubscriptionRange =
          startDateMoment.isSameOrAfter(subscriptionStartMoment, "day") &&
          startDateMoment.isSameOrBefore(subscriptionEndMoment, "day");

        // Check if end date is within subscription range (if provided)
        const isEndDateInSubscriptionRange = endDateMoment
          ? endDateMoment.isSameOrAfter(subscriptionStartMoment, "day") &&
            endDateMoment.isSameOrBefore(subscriptionEndMoment, "day")
          : true;

        if (
          !locDetail?.location_fees_covered &&
          (!isStartDateInSubscriptionRange || !isEndDateInSubscriptionRange)
        ) {
          missedDateDetails.push({
            location_name: locDetail.location_name,
            start_date: formData.startDate,
            end_date: formData.endDate,
            subscription_start_date,
            subscription_end_date,
            minStartDate,
            maxStartDate,
            minEndDate,
            maxEndDate,
          });
        }
      }

      const {
        isInvalidBudget,
        isInvalidDate,
        // invalidLoc,
        minStartDate,
        // minEndDate,
      } = joinCampaignHelper.validateEvergreenCampLocForLocSpecBudgetType(
        campaign,
        {
          budget: useBudgetRecommendation ? undefined : formData.locationBudget,
          start_date: formData.startDate,
          end_date: formData.endDate,
        },
        locDetail
      );
      if ((!useBudgetRecommendation && isInvalidBudget) || isInvalidDate) {
        const nextLocationData = {
          ...values[location],
          creatives: availableCreatives,
          ...(channelSelectionEnabled ? { channels } : {}),
          start_date: minStartDate,
          end_date: "",
          ...(useBudgetRecommendation
            ? {}
            : { budget: Number(locationBudget) }),
        };

        if (useBudgetRecommendation && availableCreatives.length) {
          appliedRecommendationLocations.push({
            loc: locDetail,
            locFormData: nextLocationData,
          });
        }

        return setFieldValue(location, nextLocationData);
      }

      // Prepare data for setFieldValue

      let brand_contribution_amount =
        values[location]?.brand_contribution_amount || 0;
      if (locationBudget) {
        brand_contribution_amount =
          joinCampaignHelper.getBrandContributionAmount({
            amount: (locationBudget as any) * 1,
            brand_contribution: campaign?.brand_contribution,
            brand_contribution_type: campaign?.brand_contribution_type,
            contribution_percentage: campaign?.contribution_percentage,
            contribution_max_limit: campaign?.contribution_max_limit,
            brand_match_preset_amount:
              (locDetail?.budget?.brand_match_preset_amount as any) * 1 || 0,
          }) || 0;
      }

      let data: Record<string, any> = {
        ...values[location],
        creatives: availableCreatives,
        ...(channelSelectionEnabled ? { channels } : {}),
        brand_contribution_amount: Number(brand_contribution_amount),
      };

      if (!hideField.startDate) {
        data.start_date = formData.startDate?.format("YYYY-MM-DD");
      }
      if (!hideField.endDate) {
        data.end_date = formData.endDate?.format("YYYY-MM-DD");
      }

      if (!hideField.locationBudget && !useBudgetRecommendation) {
        data.budget = Number(locationBudget);
      }

      if (useBudgetRecommendation && availableCreatives.length) {
        appliedRecommendationLocations.push({
          loc: locDetail,
          locFormData: data,
        });
      }

      setFieldValue(location, data);
    });

    if (useBudgetRecommendation) {
      onBulkBudgetRecommendationRequested?.(appliedRecommendationLocations);
      setCheckedLocations([]);
      return true;
    }

    fireSweetAlert({
      title: "Selections Applied",
      icon: "success",
      showCancelButton: false,
      showConfirmButton: false,
      timer: 2000,
      textAlign: "center",
    }).then(() => {
      if (isMissedCreatives) {
        displayCreativeNotApplied(missedCreativeDetails);
      } else if (missedDateDetails.length > 0) {
        displayDateNotInRange(missedDateDetails);
      }
      // Clear checked locations after successful submission
      setCheckedLocations([]);
    });

    return true; // Return true to indicate successful submission
  };

  return (
    <div className="d-flex align-items-start join-campaign-bulk-selection w-100">
      <Accordion
        expanded={shouldExpandIt("bulkSelection")}
        onChange={handleOnExpand("bulkSelection")}
        className={`location flex-grow-1`}
        elevation={2}
        sx={(theme) => ({
          border: `1px solid ${theme.palette.primary.main} !important`,
          filter: "none",
        })}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              sx={{ color: (theme) => theme.palette.primary.main }}
            />
          }
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="location-header"
        >
          {expanded ? (
            <Typography>
              1. Make selections for{" "}
              <span className="fw-b">creative, dates and budget</span>
            </Typography>
          ) : (
            <Typography>
              <Typography
                component="span"
                color="textPrimary"
                className="fw-b mr-1"
              >
                Want to make bulk selections?{" "}
              </Typography>
              <Typography component="span" className="link">
                Click here
              </Typography>
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails className="location-details">
          <JoinCampaignBulkSelectionForm
            creatives={getCreatives}
            onSubmit={handleBulkSubmit}
            campaign={campaign}
            hideField={hideField}
            subscribedLocations={subscribedLocations}
            channelSelectionEnabled={channelSelectionEnabled}
            channelOptions={channelOptions}
            creativePackages={creativePackages}
            isBudgetRecommendationAvailable={isBudgetRecommendationEnabled}
            onBulkBudgetRecommendationRequested={
              onBulkBudgetRecommendationRequested
            }
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default JoinCampaignBulkSelection;
